import React, { useEffect, useCallback } from "react";
import { ChevronDown } from "lucide-react";
import { ConversationItem } from "~/components/assistant/ConversationItem";
import { PopoverButton } from "~/components/assistant/PopoverButton";
import { useListboxKeyboardNavigation } from "~/components/assistant/hooks/useListboxKeyboardNavigation";
import { Conversation } from "~/components/assistant/types";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useConversations } from "~/contexts/conversations";

// Extract conversation ID - defined outside component for stable reference
const getConversationId = (conversation: Conversation) => conversation.id;

interface ConversationSelectorProps {
  conversations: Conversation[];
  currentConversationId: string | null;
  isCreatingConversation?: boolean;
  onSelect: (conversationId: string) => void;
  onDelete: (conversationId: string, event: React.MouseEvent) => void;
  onRename: (conversationId: string, displayName: string) => void;
}

export function ConversationSelector({
  conversations,
  currentConversationId,
  isCreatingConversation = false,
  onSelect,
  onDelete,
  onRename,
}: ConversationSelectorProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const conversationsContext = useConversations();
  const currentConversation = conversations.find((c) => c.id === currentConversationId);

  const handleSelectConversation = useCallback(
    (conversation: Conversation) => {
      onSelect(conversation.id);
      setIsOpen(false);
    },
    [onSelect]
  );

  const { focusedIndex, handleKeyDown, getFocusedItemId } = useListboxKeyboardNavigation({
    items: conversations,
    onSelect: handleSelectConversation,
    selectedItem: currentConversation || null,
    getItemId: getConversationId,
    idPrefix: "conversation-selector-",
  });

  const formatConversationLabel = (conversation: Conversation) => {
    // Use display_name if available
    if (conversation.display_name) {
      return conversation.display_name;
    }

    const date = new Date(conversation.created_at);
    const now = new Date();

    const timeString = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // Check if it's today, yesterday, or another date
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = (() => {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return date.toDateString() === yesterday.toDateString();
    })();

    let dateString: string;

    if (isToday) {
      dateString = "Today";
    } else if (isYesterday) {
      dateString = "Yesterday";
    } else {
      dateString = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }

    return `${dateString}, ${timeString}`;
  };

  useEffect(() => {
    if (conversations.length === 0) {
      setIsOpen(false);
    }
  }, [conversations]);

  const handleConversationSelect = (conversationId: string) => {
    onSelect(conversationId);
    setIsOpen(false);
  };

  const focusedItemId = getFocusedItemId();

  // If no conversations exist, just show a disabled button
  if (conversations.length === 0) {
    return (
      <PopoverButton
        variant="ghost"
        disabled={true}
        className="disabled:opacity-50 disabled:cursor-not-allowed has-[>svg]:px-2"
        aria-label="No conversations available"
      >
        <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-gray-600 text-[12px] text-left text-nowrap">
          <p className="block leading-[16px] whitespace-pre">No conversations</p>
        </div>
      </PopoverButton>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip delayDuration={700}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <div>
              <PopoverButton
                variant="ghost"
                disabled={conversations.length === 0}
                className="disabled:opacity-50 disabled:cursor-not-allowed has-[>svg]:px-2 py-2"
                aria-label="Select conversation"
              >
                <div className="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[13px] text-left text-nowrap">
                  <p className="block leading-[16px] whitespace-pre">
                    {isCreatingConversation
                      ? "Starting conversation..."
                      : currentConversation
                      ? formatConversationLabel(currentConversation)
                      : conversations.length > 0
                      ? "Select conversation"
                      : "No conversations"}
                  </p>
                </div>
                <ChevronDown className="size-4 text-gray-500" />
              </PopoverButton>
            </div>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <p>Chat History</p>
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="w-[230px] p-2" align="start">
        <div
          role="listbox"
          aria-label="Select a conversation"
          {...(focusedItemId ? { "aria-activedescendant": focusedItemId } : {})}
          onKeyDown={handleKeyDown}
          className="max-h-[450px] overflow-y-auto"
        >
          {conversations.length > 0 ? (
            conversations.map((conversation, index) => (
              <ConversationItem
                key={conversation.id}
                id={`conversation-selector-${conversation.id}`}
                conversation={conversation}
                isSelected={conversation.id === currentConversationId}
                isFocused={index === focusedIndex}
                isStreaming={conversationsContext?.isStreaming(conversation.id) ?? false}
                showCheckmark={true}
                onSelect={handleConversationSelect}
                onDelete={onDelete}
                onRename={onRename}
                formatLabel={formatConversationLabel}
              />
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground text-center">
              No conversations available
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
